import React, { useState, useRef } from 'react';
import { 
  Upload, Download, FileSpreadsheet, X, Check, AlertCircle, 
  ChevronRight, ArrowRight, RefreshCw, FileText, CheckCircle2,
  XCircle, AlertTriangle, Loader2, FileDown, Table, File,
  Zap, Settings, HelpCircle, Sparkles
} from 'lucide-react';
import { leadsImportExportAPI, leadsAPI } from '../services/api';
import LeadAdvancedExportModal from './LeadAdvancedExportModal';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Step indicator component
const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center mb-8">
    {steps.map((step, idx) => (
      <React.Fragment key={step}>
        <div className={`flex items-center ${idx <= currentStep ? 'text-teal-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 ${
            idx < currentStep 
              ? 'bg-teal-600 border-teal-600 text-white' 
              : idx === currentStep 
                ? 'border-teal-600 text-teal-600' 
                : 'border-gray-300 text-gray-400'
          }`}>
            {idx < currentStep ? <Check size={16} /> : idx + 1}
          </div>
          <span className="ml-2 text-sm font-medium hidden sm:inline">{step}</span>
        </div>
        {idx < steps.length - 1 && (
          <ChevronRight className="mx-4 text-gray-300" size={20} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Mapping type badge component
const MappingTypeBadge = ({ type }) => {
  const styles = {
    predefined: {
      bg: 'bg-green-100',
      text: 'text-green-700',
      icon: Zap,
      label: 'Auto-matched'
    },
    auto: {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      icon: Sparkles,
      label: 'Smart-matched'
    },
    ota: {
      bg: 'bg-purple-100',
      text: 'text-purple-700',
      icon: FileSpreadsheet,
      label: 'OTA Data'
    },
    manual: {
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      icon: Settings,
      label: 'Needs Review'
    }
  };
  
  const style = styles[type] || styles.manual;
  const Icon = style.icon;
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
      <Icon size={10} className="mr-1" />
      {style.label}
    </span>
  );
};

// Field mapping row component - ENHANCED with all CRM sections
const MappingRow = ({ mapping, crmFields, onMappingChange, usedFields }) => {
  const availableFields = crmFields.filter(
    f => !usedFields.includes(f.field) || mapping.crm_field === f.field
  );
  
  // Group fields by section for better organization
  const groupedFields = availableFields.reduce((acc, field) => {
    const section = field.section || 'other';
    if (!acc[section]) acc[section] = [];
    acc[section].push(field);
    return acc;
  }, {});
  
  // Section labels for dropdown groups - ALL sections from Original CRM
  const sectionLabels = {
    lead_information: 'Lead Information',
    address_information: 'Address Information',
    ota_challenges: 'OTA & Challenges',
    payment_information: 'Commercials',
    property_details: 'Property Details',
    document_collection: 'Document Collection',
    property_checklist: 'Property Checklist',
    bank_details: 'Bank Details',
    ota_information: 'OTA Information',
    tasks: 'Tasks',
    notes: 'Notes',
    visits: 'Visits',
    calls: 'Calls',
    history: 'History',
    other: 'Other'
  };
  
  // Section order for consistent display
  const sectionOrder = [
    'lead_information',
    'address_information',
    'ota_challenges',
    'payment_information',
    'property_details',
    'document_collection',
    'property_checklist',
    'bank_details',
    'ota_information',
    'tasks',
    'notes',
    'visits',
    'calls',
    'history',
    'other'
  ];
  
  const getMappingStatusStyle = () => {
    if (mapping.crm_field) {
      if (mapping.mapping_type === 'predefined') {
        return 'border-green-300 bg-green-50';
      } else if (mapping.mapping_type === 'auto') {
        return 'border-blue-300 bg-blue-50';
      } else if (mapping.mapping_type === 'ota') {
        return 'border-purple-300 bg-purple-50';
      }
      return 'border-teal-300 bg-teal-50';
    }
    return 'border-gray-300';
  };
  
  return (
    <div className="grid grid-cols-12 gap-4 items-center py-3 border-b border-gray-100 last:border-0">
      <div className="col-span-4">
        <div className="font-medium text-gray-800 flex items-center gap-2">
          {mapping.csv_column}
          {mapping.mapping_type && (
            <MappingTypeBadge type={mapping.mapping_type} />
          )}
        </div>
        <div className="text-xs text-gray-500 truncate mt-1">
          {mapping.sample_values?.slice(0, 2).join(', ') || 'No samples'}
        </div>
      </div>
      <div className="col-span-1 flex justify-center">
        <ArrowRight size={18} className="text-gray-400" />
      </div>
      <div className="col-span-5">
        <select
          value={mapping.crm_field || ''}
          onChange={(e) => onMappingChange(mapping.csv_column, e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none transition-colors ${getMappingStatusStyle()}`}
          data-testid={`mapping-${mapping.csv_column}`}
        >
          <option value="">-- Ignore this column --</option>
          {/* Render fields in section order for consistent display */}
          {sectionOrder.filter(section => groupedFields[section]?.length > 0).map(section => (
            <optgroup key={section} label={sectionLabels[section] || section}>
              {groupedFields[section].map(field => (
                <option key={field.field} value={field.field}>
                  {field.label} {field.required && '*'}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <div className="col-span-2 flex justify-center">
        {mapping.crm_field ? (
          <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
            <Check size={12} className="mr-1" /> Mapped
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs">
            Ignore
          </span>
        )}
      </div>
    </div>
  );
};

// Import Report component - ENHANCED with updated leads and auto-creation summary
const ImportReport = ({ results }) => {
  const { success, updated, skipped, failed, errors, duplicates, total_rows, lifecycle_created, properties_created } = results;
  const total = (success || 0) + (updated || 0) + (skipped || 0) + (failed || 0);
  
  // Separate duplicates by action
  const updatedDuplicates = duplicates?.filter(d => d.action === 'updated') || [];
  const skippedDuplicates = duplicates?.filter(d => d.action === 'skipped' || d.action === 'skipped_duplicate_in_file') || [];
  
  return (
    <div className="space-y-6">
      {/* Auto-Creation Summary Banner */}
      {(lifecycle_created > 0 || properties_created > 0) && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4" data-testid="auto-creation-banner">
          <div className="flex items-center gap-3">
            <Zap className="text-teal-600 flex-shrink-0" size={22} />
            <div>
              <div className="font-semibold text-teal-800">Closed Won Auto-Creation</div>
              <div className="text-sm text-teal-600 mt-1">
                {properties_created > 0 && <span>{properties_created} {properties_created === 1 ? 'property' : 'properties'} created in Properties tab. </span>}
                {lifecycle_created > 0 && <span>{lifecycle_created} {lifecycle_created === 1 ? 'entry' : 'entries'} created in Hotel Lifecycle tab.</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <CheckCircle2 className="mx-auto mb-2 text-green-600" size={28} />
          <div className="text-2xl font-bold text-green-700">{success || 0}</div>
          <div className="text-sm text-green-600">New Imports</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
          <RefreshCw className="mx-auto mb-2 text-blue-600" size={28} />
          <div className="text-2xl font-bold text-blue-700">{updated || 0}</div>
          <div className="text-sm text-blue-600">Updated</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
          <AlertTriangle className="mx-auto mb-2 text-amber-600" size={28} />
          <div className="text-2xl font-bold text-amber-700">{skipped || 0}</div>
          <div className="text-sm text-amber-600">Skipped</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
          <XCircle className="mx-auto mb-2 text-red-600" size={28} />
          <div className="text-2xl font-bold text-red-700">{failed || 0}</div>
          <div className="text-sm text-red-600">Failed</div>
        </div>
      </div>
      
      {/* Total Summary */}
      <div className="text-center text-sm text-gray-600">
        Total Rows Processed: <span className="font-semibold">{total_rows || total}</span>
      </div>
      
      {/* Progress Bar */}
      <div className="bg-gray-100 rounded-full h-3 overflow-hidden">
        <div className="h-full flex">
          <div 
            className="bg-green-500 transition-all" 
            style={{ width: `${total > 0 ? ((success || 0) / total) * 100 : 0}%` }} 
          />
          <div 
            className="bg-blue-500 transition-all" 
            style={{ width: `${total > 0 ? ((updated || 0) / total) * 100 : 0}%` }} 
          />
          <div 
            className="bg-amber-500 transition-all" 
            style={{ width: `${total > 0 ? ((skipped || 0) / total) * 100 : 0}%` }} 
          />
          <div 
            className="bg-red-500 transition-all" 
            style={{ width: `${total > 0 ? ((failed || 0) / total) * 100 : 0}%` }} 
          />
        </div>
      </div>
      
      {/* Updated Leads List */}
      {updatedDuplicates.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
            <RefreshCw size={18} className="mr-2" /> Updated Records ({updatedDuplicates.length})
          </h4>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {updatedDuplicates.slice(0, 10).map((dup, idx) => (
              <div key={idx} className="text-sm text-blue-700 flex items-start">
                <span className="font-medium mr-2">Row {dup.row}:</span>
                <span>{dup.hotel_name} ({dup.phone}) - Updated existing: {dup.existing_hotel}</span>
              </div>
            ))}
            {updatedDuplicates.length > 10 && (
              <div className="text-sm text-blue-600 font-medium">
                ... and {updatedDuplicates.length - 10} more updates
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Errors List */}
      {errors?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <h4 className="font-semibold text-red-800 mb-3 flex items-center">
            <XCircle size={18} className="mr-2" /> Failed Records ({errors.length})
          </h4>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {errors.slice(0, 20).map((err, idx) => (
              <div key={idx} className="text-sm text-red-700 flex items-start">
                <span className="font-medium mr-2">Row {err.row}:</span>
                <span>{err.reason}</span>
              </div>
            ))}
            {errors.length > 20 && (
              <div className="text-sm text-red-600 font-medium">
                ... and {errors.length - 20} more errors
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Skipped Duplicates List */}
      {skippedDuplicates.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h4 className="font-semibold text-amber-800 mb-3 flex items-center">
            <AlertTriangle size={18} className="mr-2" /> Skipped Duplicates ({skippedDuplicates.length})
          </h4>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {skippedDuplicates.slice(0, 10).map((dup, idx) => (
              <div key={idx} className="text-sm text-amber-700">
                Row {dup.row}: {dup.hotel_name} ({dup.phone})
              </div>
            ))}
            {skippedDuplicates.length > 10 && (
              <div className="text-sm text-amber-600 font-medium">
                ... and {skippedDuplicates.length - 10} more duplicates
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Import/Export Modal Component
const LeadImportExportModal = ({ isOpen, onClose, onImportComplete, currentFilters = {}, selectedIds = [] }) => {
  const [mode, setMode] = useState(null); // 'import' or 'export'
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Import state
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [updateDuplicates, setUpdateDuplicates] = useState(true); // NEW: Update duplicates by default
  const [importResults, setImportResults] = useState(null);
  
  const fileInputRef = useRef(null);
  
  const resetState = () => {
    setMode(null);
    setStep(0);
    setSelectedFile(null);
    setPreviewData(null);
    setMappings([]);
    setUpdateDuplicates(true);
    setImportResults(null);
    setError(null);
  };
  
  const handleClose = () => {
    resetState();
    onClose();
  };
  
  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validTypes.includes(ext)) {
      setError('Please select a CSV or Excel file');
      return;
    }
    
    setSelectedFile(file);
    setError(null);
    
    // Preview file
    try {
      setLoading(true);
      const response = await leadsImportExportAPI.previewImport(file);
      setPreviewData(response.data);
      
      // Initialize mappings from smart suggestions (with mapping_type preserved)
      const initialMappings = response.data.suggested_mappings.map(m => ({
        ...m,
        crm_field: m.suggested_field || ''
      }));
      setMappings(initialMappings);
      
      setStep(1);
    } catch (err) {
      console.error('Preview error:', err);
      setError(err.response?.data?.detail || 'Failed to read file');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle mapping change
  const handleMappingChange = (csvColumn, crmField) => {
    setMappings(prev => prev.map(m => 
      m.csv_column === csvColumn 
        ? { ...m, crm_field: crmField, mapping_type: crmField ? 'manual' : null } 
        : m
    ));
  };
  
  // Execute import
  const handleImport = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await leadsImportExportAPI.importLeads(
        selectedFile,
        mappings.filter(m => m.crm_field),
        !updateDuplicates  // Invert: skip_duplicates=true means DON'T update
      );
      
      setImportResults(response.data.results);
      setStep(2);
      
      // Notify parent to refresh leads list
      const totalSuccess = (response.data.results.success || 0) + (response.data.results.updated || 0);
      if (onImportComplete && totalSuccess > 0) {
        onImportComplete();
      }
    } catch (err) {
      console.error('Import error:', err);
      setError(err.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Simple / Overall single-sheet account export
  const handleAccountExport = async (scope) => {
    try {
      setLoading(true);
      setError(null);
      const response = await leadsImportExportAPI.exportAccount(scope);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      let filename = `accounts_${scope}_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      const cd = response.headers?.['content-disposition'];
      if (cd) { const m = cd.match(/filename="(.+)"/); if (m) filename = m[1]; }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      handleClose();
    } catch (err) {
      console.error('Account export error:', err);
      setError(err.response?.data?.detail || 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle export (CSV or Excel)
  const handleExport = async (format = 'csv') => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await leadsImportExportAPI.exportLeads(format);
      
      // Create download link
      const mimeType = format === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv';
      const ext = format === 'excel' ? 'xlsx' : 'csv';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate
      const contentDisposition = response.headers?.['content-disposition'];
      let filename = `leads_export_${new Date().toISOString().split('T')[0]}.${ext}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      handleClose();
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.detail || 'Export failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle PDF export with jsPDF
  const handleExportPDF = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch leads data
      const response = await leadsAPI.getAll({ limit: 10000 });
      const leads = response.data?.data || [];
      
      if (leads.length === 0) {
        setError('No leads to export');
        setLoading(false);
        return;
      }
      
      // Create PDF document
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      // Add title
      doc.setFontSize(20);
      doc.setTextColor(13, 148, 136); // Teal color
      doc.text('Leads Export Report', 14, 15);
      
      // Add metadata
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
      doc.text(`Total Records: ${leads.length}`, 14, 27);
      
      // Prepare table data
      const tableHeaders = [
        'Hotel Name', 'Owner', 'Phone', 'Email', 'City', 'State', 
        'Stage', 'Status', 'Source'
      ];
      
      const tableData = leads.map(lead => [
        lead.hotel_name || '-',
        lead.owner_name || '-',
        lead.phone || '-',
        lead.email || '-',
        lead.city || '-',
        lead.state || '-',
        lead.lead_stage || '-',
        lead.property_status || '-',
        lead.source || '-'
      ]);
      
      // Generate table using autoTable
      doc.autoTable({
        head: [tableHeaders],
        body: tableData,
        startY: 32,
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [13, 148, 136],
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        margin: { left: 14, right: 14 },
        didDrawPage: (data) => {
          // Add page number
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.width - 25,
            doc.internal.pageSize.height - 10
          );
        }
      });
      
      // Save PDF
      const filename = `leads_export_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
      
      handleClose();
    } catch (err) {
      console.error('PDF Export error:', err);
      setError(err.message || 'PDF export failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const response = await leadsImportExportAPI.downloadTemplate();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'leads_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Template download error:', err);
    }
  };
  
  if (!isOpen) return null;
  
  const usedFields = mappings.filter(m => m.crm_field).map(m => m.crm_field);
  const hasMappedFields = usedFields.length > 0;
  const hasHotelName = usedFields.includes('hotel_name');

  return (
    <>
    <LeadAdvancedExportModal
      isOpen={showAdvanced}
      onClose={() => { setShowAdvanced(false); handleClose(); }}
      currentFilters={currentFilters}
      selectedIds={selectedIds}
    />
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center">
            <FileSpreadsheet className="mr-3 text-teal-600" size={24} />
            {mode === 'import' ? 'Import Leads' : mode === 'export' ? 'Export Leads' : 'Import / Export Leads'}
          </h2>
          <button 
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
            data-testid="close-import-export-modal"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Mode Selection — Simple Export / Overall Export / Import Leads */}
          {!mode && (
            <div className="space-y-6">
              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="mx-auto mb-4 text-teal-600 animate-spin" size={48} />
                  <p className="text-gray-600">Preparing export...</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <button
                      onClick={() => handleAccountExport('simple')}
                      className="p-6 border-2 border-gray-200 rounded-2xl hover:border-teal-500 hover:bg-teal-50 transition-all group text-left"
                      data-testid="select-simple-export"
                    >
                      <FileSpreadsheet className="mb-3 text-teal-600" size={36} />
                      <h3 className="text-base font-bold text-gray-800 mb-1">Simple Export</h3>
                      <p className="text-sm text-gray-500">
                        Single-sheet Excel with all account data — lead info, commercials, document checklist, bank details &amp; OTA information.
                      </p>
                    </button>

                    <button
                      onClick={() => handleAccountExport('overall')}
                      className="p-6 border-2 border-gray-200 rounded-2xl hover:border-teal-500 hover:bg-teal-50 transition-all group text-left"
                      data-testid="select-overall-export"
                    >
                      <Table className="mb-3 text-teal-600" size={36} />
                      <h3 className="text-base font-bold text-gray-800 mb-1">Overall Export</h3>
                      <p className="text-sm text-gray-500">
                        Everything in Simple plus full operational history — listing &amp; QC workflow, master form, commercial approval stages &amp; timeline.
                      </p>
                    </button>

                    <button
                      onClick={() => setMode('import')}
                      className="p-6 border-2 border-gray-200 rounded-2xl hover:border-teal-500 hover:bg-teal-50 transition-all group text-left"
                      data-testid="select-import-mode"
                    >
                      <Upload className="mb-3 text-teal-600" size={36} />
                      <h3 className="text-base font-bold text-gray-800 mb-1">Import Leads</h3>
                      <p className="text-sm text-gray-500">
                        Upload a CSV or Excel file to import or update accounts, with smart field mapping.
                      </p>
                    </button>
                  </div>

                  {/* More Export Options — preserves Advanced / Full / CSV / PDF */}
                  <div className="text-center pt-2">
                    <button
                      onClick={() => setMode('export')}
                      className="text-sm text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1.5"
                      data-testid="more-export-options-btn"
                    >
                      <Settings size={15} /> More Export Options (Advanced, Full CRM, CSV, PDF)
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* Export Format Selection */}
          {mode === 'export' && !loading && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 text-center">Choose Export Format</h3>

              {/* Advanced Export — primary CTA */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-300 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className="text-teal-600" size={22} />
                  <h4 className="font-semibold text-teal-800">Advanced Export — 162 columns, 12 modes</h4>
                </div>
                <p className="text-sm text-teal-700 mb-3">
                  Audit, fallback-fill, custom-column, date-range, OTA-specific, duplicates &amp; more — with a post-export summary report.
                </p>
                <button
                  onClick={() => setShowAdvanced(true)}
                  data-testid="open-advanced-export-btn"
                  className="w-full py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center justify-center gap-2"
                >
                  <Sparkles size={16} /> Open Advanced Export
                </button>
              </div>

              {/* Full Export Highlight */}
              <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <FileSpreadsheet className="text-teal-600" size={24} />
                  <h4 className="font-semibold text-teal-800">Quick Full CRM Export</h4>
                </div>
                <p className="text-sm text-teal-700 mb-3">
                  Download a comprehensive Excel file with 9 sheets containing all CRM data:
                  Leads, Payment Info, Bank Details, OTA Information, Tasks, Notes, Visits, Calls, and History.
                </p>
                <button
                  onClick={() => handleExport('excel')}
                  className="w-full py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center justify-center gap-2"
                  data-testid="export-full-excel-btn"
                >
                  <Download size={18} />
                  Download Full Export (Excel)
                </button>
              </div>
              
              <div className="text-center text-sm text-gray-500 mb-2">Or choose a simpler format:</div>
              
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleExport('csv')}
                  className="p-5 border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all group text-center"
                  data-testid="export-csv-btn"
                >
                  <FileText className="mx-auto mb-2 text-green-600" size={32} />
                  <h4 className="font-semibold text-gray-800">CSV</h4>
                  <p className="text-xs text-gray-500 mt-1">Basic lead data only</p>
                </button>
                
                <button
                  onClick={() => handleExportPDF()}
                  className="p-5 border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all group text-center"
                  data-testid="export-pdf-btn"
                >
                  <File className="mx-auto mb-2 text-red-600" size={32} />
                  <h4 className="font-semibold text-gray-800">PDF</h4>
                  <p className="text-xs text-gray-500 mt-1">Printable lead report</p>
                </button>
              </div>
              
              <div className="text-center pt-4">
                <button
                  onClick={() => setMode(null)}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  ← Back to options
                </button>
              </div>
            </div>
          )}
          
          {/* Import Flow */}
          {mode === 'import' && (
            <>
              <StepIndicator 
                currentStep={step} 
                steps={['Upload File', 'Map Fields', 'Results']} 
              />
              
              {/* Step 0: File Upload */}
              {step === 0 && (
                <div className="text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-2xl p-12 cursor-pointer hover:border-teal-500 hover:bg-teal-50 transition-all"
                  >
                    {loading ? (
                      <Loader2 className="mx-auto mb-4 text-teal-600 animate-spin" size={48} />
                    ) : (
                      <Upload className="mx-auto mb-4 text-gray-400" size={48} />
                    )}
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {loading ? 'Reading file...' : 'Click to upload or drag and drop'}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Supports CSV and Excel files (up to 10,000+ records)
                    </p>
                    {selectedFile && (
                      <div className="inline-flex items-center px-4 py-2 bg-teal-100 text-teal-800 rounded-full">
                        <FileText size={16} className="mr-2" />
                        {selectedFile.name}
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-6">
                    <button
                      onClick={handleDownloadTemplate}
                      className="text-teal-600 hover:text-teal-700 text-sm flex items-center justify-center mx-auto"
                    >
                      <Download size={16} className="mr-1" />
                      Download template file
                    </button>
                  </div>
                </div>
              )}
              
              {/* Step 1: Field Mapping */}
              {step === 1 && previewData && (
                <div className="space-y-6">
                  {/* File Info */}
                  <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <FileSpreadsheet className="text-teal-600 mr-3" size={24} />
                      <div>
                        <p className="font-medium text-gray-800">{previewData.filename}</p>
                        <p className="text-sm text-gray-500">{previewData.total_rows} rows detected</p>
                      </div>
                    </div>
                    <button
                      onClick={() => { setStep(0); setSelectedFile(null); setPreviewData(null); }}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Change file
                    </button>
                  </div>
                  
                  {/* Smart Mapping Stats */}
                  {previewData.mapping_stats && (
                    <div className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-xl p-4">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                          <Sparkles className="text-teal-600" size={20} />
                          <span className="font-medium text-gray-800">Smart Mapping Results</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm">
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            <span className="text-green-700">{previewData.mapping_stats.predefined || 0} Auto-matched</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                            <span className="text-blue-700">{previewData.mapping_stats.auto || 0} Smart-matched</span>
                          </span>
                          {previewData.mapping_stats.ota > 0 && (
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                              <span className="text-purple-700">{previewData.mapping_stats.ota} OTA Data</span>
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                            <span className="text-amber-700">{previewData.mapping_stats.manual || 0} Need Review</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Mapping Instructions */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Review & Adjust:</strong> The system has auto-mapped columns based on common patterns. 
                      Verify the mappings below and adjust if needed. Columns set to "Ignore" will be skipped. 
                      <strong> Hotel Name is required.</strong>
                    </p>
                  </div>
                  
                  {/* Field Mappings */}
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase">
                      <div className="col-span-4">CSV Column</div>
                      <div className="col-span-1"></div>
                      <div className="col-span-5">CRM Field</div>
                      <div className="col-span-2 text-center">Status</div>
                    </div>
                    <div className="px-4 max-h-64 overflow-y-auto">
                      {mappings.map(mapping => (
                        <MappingRow
                          key={mapping.csv_column}
                          mapping={mapping}
                          crmFields={previewData.crm_fields}
                          onMappingChange={handleMappingChange}
                          usedFields={usedFields}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Duplicate Handling Options */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                      <Settings size={16} />
                      Duplicate Handling (based on Mobile Number)
                    </h4>
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-white transition-colors">
                        <input
                          type="radio"
                          name="duplicateHandling"
                          checked={updateDuplicates}
                          onChange={() => setUpdateDuplicates(true)}
                          className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                          data-testid="update-duplicates-radio"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-800">Update existing leads</span>
                          <p className="text-xs text-gray-500">If a lead with the same mobile number exists, update it with new data</p>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-white transition-colors">
                        <input
                          type="radio"
                          name="duplicateHandling"
                          checked={!updateDuplicates}
                          onChange={() => setUpdateDuplicates(false)}
                          className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                          data-testid="skip-duplicates-radio"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-800">Skip duplicate leads</span>
                          <p className="text-xs text-gray-500">If a lead with the same mobile number exists, skip the row</p>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  {/* Validation Warning */}
                  {!hasHotelName && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                      <AlertCircle className="text-amber-500 flex-shrink-0" size={20} />
                      <p className="text-sm text-amber-800">
                        <strong>Warning:</strong> You haven't mapped a column to "Hotel Name" which is required. 
                        Rows without a hotel name will be skipped.
                      </p>
                    </div>
                  )}
                  
                  {/* Preview Table */}
                  <div>
                    <h4 className="font-medium text-gray-800 mb-3">Data Preview (first 5 rows)</h4>
                    <div className="overflow-x-auto border border-gray-200 rounded-xl">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            {previewData.columns.map(col => (
                              <th key={col} className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.preview_data?.map((row, idx) => (
                            <tr key={idx} className="border-t border-gray-100">
                              {previewData.columns.map(col => (
                                <td key={col} className="px-3 py-2 text-gray-700 whitespace-nowrap">
                                  {row[col] || '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Step 2: Results */}
              {step === 2 && importResults && (
                <ImportReport results={importResults} />
              )}
            </>
          )}
          
          {/* Export Loading */}
          {mode === 'export' && loading && (
            <div className="text-center py-12">
              <Loader2 className="mx-auto mb-4 text-teal-600 animate-spin" size={48} />
              <p className="text-gray-600">Exporting leads...</p>
            </div>
          )}
          
          {/* Error Display */}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        {mode === 'import' && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between">
            <button
              onClick={() => {
                if (step > 0 && step < 2) setStep(step - 1);
                else if (step === 0) { resetState(); }
                else handleClose();
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
            >
              {step === 2 ? 'Close' : 'Back'}
            </button>
            
            {step === 1 && (
              <button
                onClick={handleImport}
                disabled={loading || !hasMappedFields}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="start-import-btn"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload size={18} className="mr-2" />
                    Import {previewData?.total_rows || 0} Leads
                  </>
                )}
              </button>
            )}
            
            {step === 2 && (
              <button
                onClick={() => { resetState(); onImportComplete?.(); }}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center"
              >
                <RefreshCw size={18} className="mr-2" />
                Import More
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default LeadImportExportModal;
